import { queryOptions, skipToken, useQuery } from "@tanstack/react-query";

import {
  enrollmentFlagStateOf,
  NO_ENROLLMENT_FLAGS,
} from "../../utils/enrollmentFlagLevel";
import { rateQueryKeys } from "../../utils/query-keys";
import { currentSchedules } from "../../utils/rateSets";

import { EnrollmentFlagProvider } from "./EnrollmentFlagContext";

import type { FC, ReactNode } from "react";
import type { RateSchedule } from "../../types";
import type { BenefitPlan, BenefitPlanGroup } from "../../types/models";

/**
 * The plan's current schedules as the rate cells hold them. A rate save reports the group's
 * flags into this cache, so reading it shows them without a reload. A plan whose cache is not
 * yet seeded falls back to the schedules the Quotes read embedded.
 */
const useCurrentSchedules = (plan: BenefitPlan): RateSchedule[] => {
  const { data } = useQuery(
    queryOptions<RateSchedule[]>({
      queryKey: rateQueryKeys.schedules(plan.id),
      queryFn: skipToken,
    }),
  );

  return currentSchedules(data ?? plan.rateSchedule);
};

export interface GroupEnrollmentFlagProviderProps {
  /** The plan group the plan belongs to; absent for a plan that is not on the Quotes read. */
  group: BenefitPlanGroup | undefined;
  plan: BenefitPlan;
  children: ReactNode;
}

/**
 * Gives a plan's enrolled cells the server's flags for its group: from the Quotes read, and
 * from the last rate save that reported newer ones.
 */
export const GroupEnrollmentFlagProvider: FC<
  GroupEnrollmentFlagProviderProps
> = ({ group, plan, children }) => {
  const planSchedules = useCurrentSchedules(plan);

  return (
    <EnrollmentFlagProvider
      state={
        group
          ? enrollmentFlagStateOf(group, planSchedules)
          : NO_ENROLLMENT_FLAGS
      }
    >
      {children}
    </EnrollmentFlagProvider>
  );
};
